/**
 * 流式文本 → 流式音频 客户端
 *
 * 通过 WebSocket 连接 tts-server 的 /stream-tts 端点，
 * 将大模型流式输出的文本实时转换为音频（PCM base64）。
 *
 * 使用方式：
 *
 *   const tts = await connectStreamTTS({
 *     onAudio({ audio, sampling, subtitle }) {
 *       // audio:    base64 编码的 PCM 音频
 *       // sampling: 采样率（通常 24000）
 *       // subtitle: 字幕数组 [{ Word, Start, End }, ...]
 *     },
 *     onDone() {
 *       console.log('所有音频接收完毕')
 *     },
 *   })
 *
 *   // 对接大模型流式输出
 *   for await (const chunk of llmStream) {
 *     const isLast = chunk.done
 *     tts.sendText(chunk.text, isLast)
 *   }
 */

/**
 * @typedef {Object} AudioMessage
 * @property {string}  audio         - base64 编码的 PCM 音频数据
 * @property {number}  sampling      - 采样率（通常 24000）
 * @property {number}  seqNo         - 子句序号
 * @property {boolean} sentenceStart - 子句开始
 * @property {boolean} sentenceFinal - 子句结束
 * @property {boolean} final         - 整句结束
 * @property {Array}   subtitle      - 字幕信息
 */

/**
 * @typedef {Object} ReplyMessage
 * @property {string}  replyDisplay - 展示内容
 * @property {string}  replyType    - 回复类型
 * @property {number}  seqNo        - 子句序号
 * @property {boolean} isFinal      - 是否最后一句
 * @property {number}  contentType  - 内容类型
 * @property {boolean} ttsSupport   - 是否播报
 */

/**
 * @typedef {Object} StreamTTSHandle
 * @property {(text: string, isFinal?: boolean) => void} sendText  - 发送文本分片
 * @property {() => void}                                 close     - 关闭连接
 * @property {number}                                     readyState - WebSocket 状态
 */

/**
 * 连接流式 TTS 服务
 *
 * @param {Object}                         options
 * @param {string}                         [options.wsUrl]     - WebSocket 地址，默认自动推断
 * @param {(msg: AudioMessage) => void}    [options.onAudio]   - 收到音频分片
 * @param {(msg: ReplyMessage) => void}    [options.onReply]   - 收到文本回复
 * @param {() => void}                     [options.onDone]    - 全部音频接收完毕
 * @param {(err: Error) => void}           [options.onError]   - 错误回调
 * @param {() => void}                     [options.onReady]   - 连接就绪
 * @returns {Promise<StreamTTSHandle>}     连接就绪后 resolve
 */
export function connectStreamTTS({
  wsUrl,
  onAudio,
  onReply,
  onDone,
  onError,
  onReady,
} = {}) {
  const url = wsUrl || buildDefaultWsUrl()

  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url)
    let seq = 1
    let resolved = false
    let doneEmitted = false

    const handle = {
      sendText(text, isFinal = false) {
        if (ws.readyState !== WebSocket.OPEN) return
        ws.send(JSON.stringify({ text, seq: seq++, isFinal }))
      },
      close() {
        ws.close()
      },
      get readyState() {
        return ws.readyState
      },
    }

    ws.onmessage = (event) => {
      let msg
      try { msg = JSON.parse(event.data) } catch { return }

      switch (msg.type) {
        case 'ready':
          if (!resolved) {
            resolved = true
            onReady?.()
            resolve(handle)
          }
          break

        case 'audio':
          onAudio?.(msg)
          break

        case 'reply':
          onReply?.(msg)
          break

        case 'done':
          if (!doneEmitted) {
            doneEmitted = true
            onDone?.()
          }
          break

        case 'error':
          onError?.(new Error(msg.message || '未知错误'))
          if (!resolved) {
            resolved = true
            reject(new Error(msg.message))
          }
          break

        case 'closed':
          if (!doneEmitted) {
            doneEmitted = true
            onDone?.()
          }
          break
      }
    }

    ws.onerror = () => {
      const err = new Error('流式 TTS WebSocket 连接失败')
      onError?.(err)
      if (!resolved) { resolved = true; reject(err) }
    }

    ws.onclose = () => {
      if (!resolved) { resolved = true; reject(new Error('连接关闭')) }
      if (!doneEmitted) { doneEmitted = true; onDone?.() }
    }
  })
}

function buildDefaultWsUrl() {
  const apiBase = import.meta.env.VITE_NEW_API_BASE
  if (apiBase) {
    const wsBase = apiBase.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:')
    return `${wsBase.replace(/\/+$/, '')}/stream-tts`
  }
  if (typeof window !== 'undefined' && window.location) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${window.location.host}/tts-api/stream-tts`
  }
  return 'ws://127.0.0.1:3099/stream-tts'
}

/**
 * 便捷方法：一次性将完整文本以流式方式发送并收集全部音频
 * 适用于文本已经全部就绪（非流式场景）但仍希望获得流式音频输出的情况
 *
 * @param {string}   text             - 完整文本
 * @param {Object}   [options]
 * @param {number}   [options.chunkSize=20] - 每片字符数
 * @param {string}   [options.wsUrl]   - WebSocket 地址
 * @param {(msg: AudioMessage) => void} [options.onAudio] - 每片音频回调
 * @returns {Promise<AudioMessage[]>}  所有音频分片
 */
export function streamTextToAudio(text, { chunkSize = 20, wsUrl, onAudio } = {}) {
  return new Promise((resolve, reject) => {
    const audioChunks = []
    const chunks = splitText(text, chunkSize)

    connectStreamTTS({
      wsUrl,
      onAudio(msg) {
        audioChunks.push(msg)
        onAudio?.(msg)
      },
      onDone() {
        resolve(audioChunks)
      },
      onError(err) {
        reject(err)
      },
      onReady() {},
    }).then((tts) => {
      for (let i = 0; i < chunks.length; i++) {
        tts.sendText(chunks[i], i === chunks.length - 1)
      }
    }).catch(reject)
  })
}

function splitText(text, size) {
  const result = []
  for (let i = 0; i < text.length; i += size) {
    result.push(text.slice(i, i + size))
  }
  return result.length ? result : ['']
}
